/**
 * Batch #14.186 + #14.187 — Tuile SMS (paramètres admin).
 *
 * Affiche :
 *   - Section Configuration : toggle activer/désactiver + sender override
 *   - Compteur live (total, mois, année)
 *   - Coût estimé (mensuel + annuel + total)
 *   - Bouton "Envoyer un SMS test"
 *   - Historique des 50 derniers envois
 */
import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import ArchiveButton from "@/components/admin/ArchiveButton";
import SmsAutomationCard from "./SmsAutomationCard";
import {
  Smartphone, Send, Loader2, RefreshCw, CheckCircle2, AlertCircle, History,
  Power, Save, Settings as SettingsIcon,
} from "lucide-react";

export default function SmsSection() {
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceBusy, setBalanceBusy] = useState(false);
  const loadBalance = async (force = false) => {
    setBalanceBusy(true);
    try { const r = await api.get(`/admin/sms/balance${force ? "?force=1" : ""}`); setBalance(r.data); }
    catch (_e) { setBalance({ error: "Solde indisponible" }); }
    finally { setBalanceBusy(false); }
  };
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTest, setShowTest] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testText, setTestText] = useState("Test SMS depuis DG Comptabilité — Twilio");
  const [sending, setSending] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [senderDraft, setSenderDraft] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [showDiag, setShowDiag] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h, cfg] = await Promise.all([
        api.get("/admin/sms/stats"),
        api.get("/admin/sms/history?limit=50"),
        api.get("/admin/sms/settings"),
      ]);
      setStats(s.data);
      setHistory(h.data?.items || []);
      setSettings(cfg.data);
      setSenderDraft(cfg.data?.sender_override || "");
      loadBalance();
    } catch (_e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!settings) return;
    const next = !settings.enabled;
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/sms/settings", { enabled: next });
      setSettings(r.data);
      toast.success(next ? "Service SMS activé" : "Service SMS désactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveSender = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/sms/settings", { sender_override: senderDraft });
      setSettings(r.data);
      toast.success(senderDraft ? `Sender mis à jour : ${r.data.effective_sender}` : "Sender remis aux valeurs par défaut");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSavingSettings(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim() || !testText.trim()) {
      toast.error("Numéro et texte requis");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/sms/test", { to: testTo, text: testText });
      toast.success(`SMS envoyé · ${r.data.message_id?.slice(0, 12) || "OK"}`);
      setShowTest(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi échoué");
    } finally {
      setSending(false);
    }
  };

  const runDiagnostic = async () => {
    setShowDiag(true);
    setDiagnostic(null);
    try {
      const r = await api.get("/admin/sms/diagnostic");
      setDiagnostic(r.data);
    } catch (e) {
      setDiagnostic({ error: e.response?.data?.detail || e.message });
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const isEnabled = !!settings?.enabled;
  const isConfigured = !!(settings?.twilio_configured ?? settings?.infobip_configured);
  const senderChanged = (senderDraft || "") !== (settings?.sender_override || "");

  return (
    <div className="space-y-5" data-testid="settings-sms-section">
      {/* ═══════════════ CONFIGURATION ═══════════════ */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <SettingsIcon className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-sm text-slate-800">Configuration du service SMS</h3>
        </div>

        <div className="p-4 space-y-4">
          {/* Toggle enabled/disabled */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Power className={`w-4 h-4 ${isEnabled ? "text-emerald-600" : "text-slate-400"}`} />
                Service SMS — {isEnabled ? "Activé" : "Désactivé"}
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {isEnabled
                  ? "Tous les workflows (chat, avis, baux) peuvent envoyer des SMS aux clients qui l'ont activé dans leurs préférences."
                  : "Aucun SMS ne sera envoyé. Les tentatives sont loggées avec statut SERVICE_DISABLED pour audit."}
              </p>
            </div>
            <button
              type="button"
              onClick={toggleEnabled}
              disabled={savingSettings || !isConfigured}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
                isEnabled ? "bg-emerald-500" : "bg-slate-300"
              }`}
              data-testid="settings-sms-toggle"
              title={isConfigured ? (isEnabled ? "Cliquer pour désactiver" : "Cliquer pour activer") : "Configurer Twilio env vars d'abord"}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  isEnabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Sender override */}
          <div className="border-t border-slate-100 pt-4">
            <label className="text-xs font-semibold text-slate-700">Numéro d'expéditeur (sender Twilio)</label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
              Par défaut : <code className="font-mono text-slate-700 bg-slate-100 px-1 rounded">{settings?.env_sender || "—"}</code> (numéro Twilio provisionné dans les variables d'environnement).
              Vous pouvez surcharger ici par un autre numéro Twilio que vous avez acheté (format E.164, ex. <code>+15812225525</code>).
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={senderDraft}
                onChange={(e) => setSenderDraft(e.target.value)}
                placeholder="Laisser vide pour utiliser le sender par défaut"
                className="flex-1 min-w-[200px] px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono"
                maxLength={20}
                data-testid="settings-sms-sender-input"
              />
              <button
                type="button"
                onClick={saveSender}
                disabled={savingSettings || !senderChanged}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="settings-sms-sender-save"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Sender effectif actuel : <b className="font-mono text-emerald-700">{settings?.effective_sender || "—"}</b>
              {settings?.sender_override && (
                <span className="ml-2 text-amber-700">(override en cours)</span>
              )}
            </div>
          </div>

          {/* Status config */}
          <div className={`rounded-lg p-2.5 text-xs ${isConfigured ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
            {isConfigured ? (
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Twilio API joignable · <span className="font-mono">{settings?.account_sid_preview || "AC…"}</span></span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-700">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Twilio non configuré — vérifiez les env vars <span className="font-mono">TWILIO_ACCOUNT_SID</span> + <span className="font-mono">TWILIO_AUTH_TOKEN</span> + <span className="font-mono">TWILIO_FROM_NUMBER</span></span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════ COMPTEURS / STATS ═══════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="SMS ce mois" value={stats?.month_count ?? 0} sub={`${stats?.month_cost_cad ?? 0} $ CAD`} tone="emerald" />
        <StatCard label="SMS cette année" value={stats?.year_count ?? 0} sub={`${stats?.year_cost_cad ?? 0} $ CAD`} tone="blue" />
        <StatCard label="Total envoyés" value={stats?.total_sent ?? 0} sub={`${stats?.total_cost_cad ?? 0} $ CAD`} tone="indigo" />
        <StatCard label="Total échecs" value={stats?.total_failed ?? 0} sub={`Coût/SMS : ${stats?.cost_per_unit_cad ?? 0.10} $`} tone="rose" />
        <div className={`rounded-xl border p-3 relative ${balance?.low ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-700"}`} data-testid="sms-twilio-balance">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide opacity-70">Solde Twilio</div>
            <button type="button" onClick={() => loadBalance(true)} className="opacity-60 hover:opacity-100" title="Rafraîchir le solde" data-testid="sms-twilio-balance-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${balanceBusy ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="text-xl font-bold tabular-nums" data-testid="sms-twilio-balance-value">
            {balance?.balance != null ? `${balance.balance.toFixed(2)} ${balance.currency}` : balance?.error ? "—" : "…"}
          </div>
          <div className="text-[11px] opacity-80">
            {balance?.balance != null ? `≈ ${balance.sms_left_estimate} SMS restants${balance.low ? " · solde bas, rechargez" : ""}` : balance?.error || "chargement…"}
          </div>
        </div>
      </div>

      {/* Batch #14.328 — Automatisations (confirmation RDV « OUI », pièces manquantes) */}
      <SmsAutomationCard />

      {/* ═══════════════ ACTIONS ═══════════════ */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setShowTest(true)}
          disabled={!isEnabled}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="settings-sms-test-open"
          title={isEnabled ? "" : "Service SMS désactivé — activez-le pour envoyer un test"}
        >
          <Send className="w-4 h-4" />
          Envoyer un SMS test
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          data-testid="settings-sms-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Rafraîchir
        </button>
        <button
          onClick={runDiagnostic}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold hover:bg-amber-100"
          data-testid="settings-sms-diagnostic"
          title="Diagnostic complet (env vars, connectivité Twilio)"
        >
          <AlertCircle className="w-4 h-4" />
          Diagnostic
        </button>
      </div>

      {/* Modal test */}
      {showTest && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" data-testid="settings-sms-test-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-emerald-600" /> Envoyer un SMS test
              </h3>
              <button onClick={() => setShowTest(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Numéro destinataire</label>
                <input type="tel" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                  placeholder="14181234567 ou 4181234567"
                  className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg"
                  data-testid="settings-sms-test-to" />
                <p className="text-[10px] text-slate-400 mt-0.5">10 chiffres = Canada/USA, ou format E.164 (avec +)</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Texte ({testText.length}/160)</label>
                <textarea rows={3} value={testText} onChange={(e) => setTestText(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg"
                  data-testid="settings-sms-test-text" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setShowTest(false)} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200">Annuler</button>
                <button onClick={sendTest} disabled={sending}
                  className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="settings-sms-test-send">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal diagnostic — Batch #14.190 */}
      {showDiag && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" data-testid="settings-sms-diagnostic-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" /> Diagnostic SMS
              </h3>
              <button onClick={() => setShowDiag(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            {!diagnostic ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
            ) : diagnostic.error ? (
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">{diagnostic.error}</div>
            ) : (
              <div className="space-y-3 text-sm">
                <DiagRow label="Account SID" status={diagnostic.account_sid?.set && diagnostic.account_sid?.valid_prefix && !diagnostic.account_sid?.has_whitespace}>
                  <div className="text-xs font-mono text-slate-600">
                    Longueur : {diagnostic.account_sid?.length} · Commence par : <b>{diagnostic.account_sid?.starts_with}</b>
                  </div>
                  {!diagnostic.account_sid?.valid_prefix && diagnostic.account_sid?.set && (
                    <div className="text-xs text-rose-600 mt-1">⚠ Le SID doit commencer par "AC" — valeur invalide !</div>
                  )}
                  {diagnostic.account_sid?.has_whitespace && (
                    <div className="text-xs text-rose-600 mt-1">⚠ Espaces blancs détectés !</div>
                  )}
                </DiagRow>
                <DiagRow label="Auth Token" status={diagnostic.auth_token?.set && !diagnostic.auth_token?.has_whitespace && !diagnostic.auth_token?.has_invisible_chars}>
                  <div className="text-xs font-mono text-slate-600">
                    Longueur : {diagnostic.auth_token?.length} · Préfixe : <b>{diagnostic.auth_token?.starts_with}</b>
                  </div>
                  {diagnostic.auth_token?.has_whitespace && (
                    <div className="text-xs text-rose-600 mt-1">⚠ Espaces blancs détectés dans le token !</div>
                  )}
                  {diagnostic.auth_token?.has_invisible_chars && (
                    <div className="text-xs text-rose-600 mt-1">⚠ Caractères invisibles détectés (probablement \n ou \t) !</div>
                  )}
                  {diagnostic.auth_token?.length > 0 && diagnostic.auth_token?.length !== 32 && (
                    <div className="text-xs text-amber-700 mt-1">⚠ Token Twilio devrait faire 32 caractères (longueur actuelle: {diagnostic.auth_token?.length})</div>
                  )}
                </DiagRow>
                <DiagRow label="From Number (expéditeur)" status={diagnostic.from_number?.set && diagnostic.from_number?.e164_format && !diagnostic.from_number?.has_whitespace}>
                  <div className="text-xs font-mono text-slate-600">
                    Valeur : <b>{diagnostic.from_number?.value}</b> · Longueur : {diagnostic.from_number?.length}
                  </div>
                  {!diagnostic.from_number?.e164_format && diagnostic.from_number?.set && (
                    <div className="text-xs text-rose-600 mt-1">⚠ Le numéro doit être en format E.164 (commencer par "+") — ex. +15812225525</div>
                  )}
                </DiagRow>
                <DiagRow label="Settings DB" status={diagnostic.settings?.enabled}>
                  <div className="text-xs text-slate-600">
                    Activé : <b>{diagnostic.settings?.enabled ? "OUI" : "NON"}</b> ·
                    Sender effectif : <b className="font-mono">{diagnostic.settings?.effective_sender}</b>
                    {diagnostic.settings?.sender_override && (
                      <span className="ml-1 text-amber-700">(override)</span>
                    )}
                  </div>
                </DiagRow>
                <DiagRow label="Connectivité Twilio" status={diagnostic.connectivity?.ok}>
                  <div className="text-xs text-slate-600">
                    {diagnostic.connectivity?.http_status && <>HTTP : <b>{diagnostic.connectivity?.http_status}</b><br /></>}
                    <span className="italic">{diagnostic.connectivity?.interpretation || diagnostic.connectivity?.error}</span>
                  </div>
                  {diagnostic.connectivity?.account_status && (
                    <div className="text-[10px] text-emerald-700 font-mono mt-1">
                      Account status: {diagnostic.connectivity.account_status} · Type: {diagnostic.connectivity.account_type}
                    </div>
                  )}
                </DiagRow>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  💡 <b>Interprétation</b> :
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    <li>HTTP 200 + status "active" = Compte Twilio actif, prêt à envoyer.</li>
                    <li>HTTP 401 = Account SID ou Auth Token invalide → vérifier la valeur en console Emergent.</li>
                    <li>Erreur réseau/DNS = problème de connectivité sortante (rare).</li>
                    <li>From Number doit être en format E.164 (préfixe +1 pour US/CA) et acheté/provisionné dans votre compte Twilio.</li>
                    <li>Le From Number doit aussi être enregistré pour A2P (Application-to-Person) si vous envoyez à des numéros US/CA en grand volume.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ HISTORIQUE ═══════════════ */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-sm text-slate-800">Historique ({history.length})</h3>
        </div>
        {history.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">Aucun SMS envoyé pour le moment</div>
        ) : (
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50/50">
                <div className={`mt-1 w-2 h-2 rounded-full ${
                  h.delivered ? "bg-emerald-500" :
                  h.failed_after_send ? "bg-rose-500" :
                  h.ok ? "bg-blue-400" : "bg-rose-500"
                }`} title={h.delivered ? "Livré (DLR confirmé)" : h.failed_after_send ? "Échec après envoi" : h.ok ? "Envoyé (DLR en attente)" : "Rejeté"} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-slate-700">{h.to_raw}</span>
                    <span className="text-[10px] uppercase font-semibold text-slate-400">{h.purpose}</span>
                    {/* Batch #14.194 — DLR badge prioritaire si reçu */}
                    {h.dlr_status ? (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        h.delivered ? "bg-emerald-100 text-emerald-700" :
                        h.failed_after_send ? "bg-rose-100 text-rose-700" :
                        "bg-amber-100 text-amber-700"
                      }`} title={h.dlr_description || ""}>
                        {h.dlr_status}
                      </span>
                    ) : h.api_status && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{h.api_status}</span>
                    )}
                    {(h.twilio_price_per_message || h.infobip_price_per_message) && (
                      <span className="text-[9px] text-emerald-600 font-mono">
                        {h.twilio_price_per_message || h.infobip_price_per_message} {h.twilio_currency || h.infobip_currency || ""}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{h.text_preview}</div>
                </div>
                <div className="text-[10px] text-slate-400 text-right shrink-0">
                  {h.sent_at && new Date(h.sent_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}
                  {h.dlr_received_at && (
                    <div className="text-emerald-600 text-[9px]">DLR ✓ {new Date(h.dlr_received_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}</div>
                  )}
                </div>
                <ArchiveButton itemType="sms_log" itemId={h.id} itemLabel="cet envoi SMS" onArchived={load} size="xs" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone }) {
  const toneClasses = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-xl border p-3 ${toneClasses[tone]}`}>
      <div className="text-[10px] uppercase font-bold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5 tabular-nums">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

function DiagRow({ label, status, children }) {
  return (
    <div className="flex items-start gap-2 border border-slate-200 rounded-lg p-2.5 bg-white">
      <div className={`mt-1 w-2 h-2 rounded-full ${status ? "bg-emerald-500" : "bg-rose-500"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase text-slate-700">{label}</div>
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}
